/**
 * DealVersionViewer — read-only version history for an EXECUTED docx-native deal
 * document.
 *
 * The simple counterpart to CompareRedlineView: instead of diffing two
 * versions, it lists every version and shows the SELECTED one in a read-only
 * document viewer. Any comments baked into that version's .docx body render
 * natively in the sidebar (useDocumentLoader extracts them on mount), so viewing
 * the current/signed version surfaces its comment threads with no extra wiring.
 *
 * Scoped to executed deal docs: the document is view-only, so there are no
 * rename/restore/compare affordances — click a version, view it.
 *
 * Layout mirrors compare: [ VersionHistoryPanel (single-select) | viewer ].
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DocxEditor } from '@propdocs-editor/react';
import '@propdocs-editor/react/styles.css';
import { VersionHistoryPanel } from './VersionHistoryPanel';
import type { CompareVersion } from './compareTypes';

export function DealVersionViewer({ versions }: { versions: CompareVersion[] }) {
  // Default to the current (signed) version — versions[0] is always "current".
  const [activeKey, setActiveKey] = useState<string>(versions[0]?.key ?? '');
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mountKey, setMountKey] = useState(0);

  const active = useMemo(() => versions.find((v) => v.key === activeKey), [versions, activeKey]);

  const lastLoaded = useRef<string>('');
  React.useEffect(() => {
    if (!active) return;
    if (lastLoaded.current === active.key) return;
    lastLoaded.current = active.key;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError(null);
      try {
        const b = await active.getBytes();
        if (cancelled) return;
        if (!b) throw new Error('Could not load this version.');
        // Fresh mount per version — the editor loads the document (and its
        // baked-in comments) on mount; a new key guarantees a clean reload.
        // DocxInput accepts a Uint8Array directly.
        setBytes(b);
        setMountKey((k) => k + 1);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load version.');
          setBytes(null);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  const selectVersion = useCallback((key: string) => setActiveKey(key), []);

  return (
    <div style={PAGE}>
      <div style={BODY_ROW}>
        <VersionHistoryPanel
          versions={versions}
          baseKey={activeKey}
          revKey=""
          busy={busy}
          singleSelect
          onSelect={selectVersion}
        />
        <div style={VIEWER_COL}>
          {error && <div style={ERROR_BOX}>{error}</div>}
          {bytes ? (
            <div style={VIEWER_WRAP}>
              <DocxEditor
                key={mountKey}
                documentBuffer={bytes}
                mode="viewing"
                readOnly
                compareMode
                showToolbar={false}
                showRuler={false}
                showOutlineButton={false}
                showZoomControl={false}
                renderLogo={() => null}
              />
            </div>
          ) : (
            <div style={EMPTY}>{busy ? 'Loading version…' : 'Select a version to view.'}</div>
          )}
        </div>
      </div>
    </div>
  );
}

const PAGE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  fontFamily: 'Arial, sans-serif',
};
const BODY_ROW: React.CSSProperties = { flex: 1, minHeight: 0, display: 'flex' };
const VIEWER_COL: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  position: 'relative',
};
const VIEWER_WRAP: React.CSSProperties = { flex: 1, minHeight: 0, position: 'relative' };
const ERROR_BOX: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 13,
  color: '#c5221f',
  background: '#fce8e6',
};
const EMPTY: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#5f6368',
  fontSize: 14,
  textAlign: 'center',
  padding: 24,
};
